import { useEffect, useState } from "react";
import { Wallet, AlertTriangle, ShieldCheck, ShieldAlert, Loader2, BookmarkPlus, Check, Trash2 } from "lucide-react";
import { useArcConnect } from "@/context/ArcEcosystemContext";
import { shortAddress } from "@/lib/wallet";
import { getWalletMe, postWalletMeSave, deleteWalletMe } from "@/lib/api";

/**
 * AP-5A.2 + AP-5A.2.1 — Wallet card for the `/account` page.
 *
 * Combines two independent sources:
 *   1. Backend `GET /api/wallet/me` — authoritative for the ARC
 *      contract state, the shadow-mode rule, and the saved per-user
 *      wallet address. Returns 401 when signed out.
 *   2. Client-side `useArcConnect` — the real connected browser
 *      wallet address (window.ethereum, read-only RPC).
 *
 * AP-5A.2.1 adds a single explicit "Save wallet to AssosPay" action
 * that calls `POST /api/wallet/me`. The button only appears when a
 * browser wallet is connected AND the saved address is missing or
 * differs from the browser address. The save endpoint persists ONLY
 * the public address + chain id — no signature, no transaction, no
 * token transfer, no custody.
 */
export default function WalletCard({ linked, sessionLoading, onDataChange }) {
  const {
    address, isConnected, isWrongNetwork, isPreLaunch,
  } = useArcConnect();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(linked);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState(null);

  // Lift the current wallet snapshot up so /account can render the
  // declared-vs-verified badge in the auth strip without a second
  // fetch.
  useEffect(() => {
    if (typeof onDataChange === "function") {
      onDataChange(data);
    }
  }, [data, onDataChange]);

  useEffect(() => {
    let alive = true;
    if (!linked) {
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const out = await getWalletMe();
        if (alive) setData(out);
      } catch (e) {
        if (alive) setError("network");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [linked]);

  const handleSave = async () => {
    if (!address || saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      const chainNumeric = await getCurrentChainIdSafe();
      const out = await postWalletMeSave({
        walletAddress: address,
        chainId: chainNumeric,
      });
      setData(out);
    } catch (e) {
      setSaveError("save_failed");
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (removing) return;
    setRemoving(true);
    setRemoveError(null);
    try {
      await deleteWalletMe();
      // Re-fetch the canonical GET so the card reflects empty state
      // even if the server returns idempotent `removed:false`.
      const refreshed = await getWalletMe();
      setData(refreshed);
    } catch (e) {
      setRemoveError("remove_failed");
    } finally {
      setRemoving(false);
    }
  };

  // ---------------------------------------------------------------
  // Render branches
  // ---------------------------------------------------------------

  if (sessionLoading) {
    return (
      <div
        data-testid="account-wallet-card-loading"
        className="glass rounded-md p-5 flex items-center gap-2 text-white/60"
      >
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="font-mono text-xs">checking wallet state…</span>
      </div>
    );
  }

  if (!linked) {
    return (
      <div
        data-testid="account-wallet-card-unauthed"
        data-wallet-state="unauthed"
        className="glass rounded-md p-5"
      >
        <Header />
        <p className="mt-3 font-mono text-[11px] leading-relaxed text-white/60">
          Sign in to AssosPay to see your wallet and ARC balance.
        </p>
        <div className="mt-4 flex items-center gap-2">
          <a
            href="/signin"
            data-testid="account-wallet-card-signin-btn"
            className="btn-primary text-xs"
          >
            Sign in with email
          </a>
          {(() => {
            // CRA/webpack DefinePlugin replaces `process.env.REACT_APP_*` at
            // build time; the surrounding `typeof process !== "undefined"`
            // guard is the runtime footgun that previously kept this button
            // disabled in production even when the env var was embedded.
            const startUrl = process.env.REACT_APP_ASSOS365_LINK_START_URL || "";
            const href = startUrl
              ? `${startUrl}${startUrl.includes("?") ? "&" : "?"}return_to=${encodeURIComponent(window.location.origin + "/link/callback")}`
              : undefined;
            return (
              <a
                href={href}
                data-testid="account-wallet-card-assos365-btn"
                data-link-mode={startUrl ? "enabled" : "disabled"}
                className={
                  startUrl
                    ? "btn-secondary text-xs"
                    : "btn-secondary text-xs opacity-60 pointer-events-none cursor-not-allowed"
                }
                rel="noopener noreferrer"
                title={
                  startUrl
                    ? undefined
                    : "Assos365 link entry URL is not configured for this deployment."
                }
              >
                Link with Assos365
              </a>
            );
          })()}
        </div>
        <Disclaimer />
      </div>
    );
  }

  if (loading) {
    return (
      <div
        data-testid="account-wallet-card-loading"
        className="glass rounded-md p-5 flex items-center gap-2 text-white/60"
      >
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="font-mono text-xs">loading wallet state…</span>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div
        data-testid="account-wallet-card-error"
        data-wallet-state="error"
        className="glass rounded-md p-5"
      >
        <Header />
        <p className="mt-3 font-mono text-[11px] text-[#FF3B30]">
          Wallet state temporarily unavailable. Try again shortly.
        </p>
        <Disclaimer />
      </div>
    );
  }

  // Per-card state machine. The backend tells us about the contract
  // and the balance source; the browser tells us about the connected
  // address. They are independent — a user can be signed in but have
  // no browser wallet connected.
  const contractLive = !!data.arc_contract_live;
  const balanceSource = data.balance_source || "pre_launch_shadow";
  const isShadow = balanceSource === "pre_launch_shadow" || !contractLive;

  let walletState;
  if (!isConnected) walletState = "no-browser-wallet";
  else if (isWrongNetwork) walletState = "wrong-network";
  else walletState = "connected";

  return (
    <div
      data-testid="account-wallet-card"
      data-wallet-state={walletState}
      data-balance-source={balanceSource}
      data-arc-contract-live={contractLive ? "true" : "false"}
      className="glass rounded-md p-5"
    >
      <Header />

      {/* ---- Wallet identity row ---- */}
      <div className="mt-3 flex items-start gap-3">
        <Wallet className="mt-0.5 h-4 w-4 text-white/60" />
        <div className="flex-1 min-w-0">
          {walletState === "no-browser-wallet" && (
            <>
              <p
                data-testid="account-wallet-no-browser"
                className="font-mono text-[11px] text-white/60"
              >
                No external wallet connected in this browser.
              </p>
              <p className="mt-1 font-mono text-[10px] text-white/40">
                Use <span className="text-[#00F0FF]">Connect</span>{" "}
                in the header to connect MetaMask. The connect flow does not
                sign or transact.
              </p>
              {data.wallet_connected && (
                <SavedOnlyRow
                  savedAddress={data.connected_wallet_address}
                  savedAt={data.wallet_connected_at}
                  removing={removing}
                  removeError={removeError}
                  onRemove={handleRemove}
                />
              )}
            </>
          )}
          {walletState === "wrong-network" && (
            <>
              <p
                data-testid="account-wallet-wrong-network"
                className="flex items-center gap-2 font-mono text-[11px] text-[#FFB800]"
              >
                <AlertTriangle className="h-3.5 w-3.5" />
                Wrong network — switch to BSC from the header to view ARC state.
              </p>
              <p className="mt-1 font-mono text-[10px] text-white/40 font-mono">
                Connected address: <span className="text-white/70">{shortAddress(address)}</span>
              </p>
            </>
          )}
          {walletState === "connected" && (
            <>
              <p
                data-testid="account-wallet-connected-address"
                className="font-mono text-xs text-white/85"
                title={address}
              >
                {shortAddress(address)}
              </p>
              <p className="mt-0.5 font-mono text-[10px] text-white/40">
                External wallet · BSC · read-only
              </p>
              <SaveWalletRow
                browserAddress={address}
                savedAddress={data.connected_wallet_address}
                savedAt={data.wallet_connected_at}
                saving={saving}
                saveError={saveError}
                onSave={handleSave}
                removing={removing}
                removeError={removeError}
                onRemove={handleRemove}
              />
            </>
          )}
        </div>
        {walletState === "connected" && (
          <ShieldCheck className="mt-0.5 h-4 w-4 text-[#39FF14]" />
        )}
      </div>

      {/* ---- AP-5B.3: declared/verified status badge ---- */}
      {data.wallet_connected && (
        <VerificationStatusBadge status={data.verification_status} />
      )}

      {/* ---- ARC balance row ---- */}
      <div
        data-testid="account-wallet-balance-row"
        className="mt-4 flex items-baseline gap-3 rounded border border-white/5 bg-black/30 px-3 py-2"
      >
        <span
          data-testid="account-wallet-balance-value"
          className="font-display text-2xl font-black text-white/40"
        >
          {data.arc_balance ?? "0"}
        </span>
        <span className="font-mono text-xs text-[#00F0FF]">ARC</span>
        <span
          data-testid="account-wallet-balance-badge"
          data-balance-source={balanceSource}
          className={`ml-auto rounded border px-2 py-1 font-mono text-[9px] uppercase tracking-[0.2em] ${
            isShadow
              ? "border-[#00F0FF]/40 bg-[#00F0FF]/5 text-[#00F0FF]"
              : "border-white/20 bg-white/5 text-white/60"
          }`}
        >
          {isShadow
            ? "Pre-launch · contract not deployed"
            : balanceSource === "unavailable"
              ? "Balance temporarily unavailable"
              : "Last attestation"}
        </span>
      </div>

      <Disclaimer pre={isShadow || isPreLaunch} />
    </div>
  );
}

function Header() {
  return (
    <div className="flex items-center justify-between">
      <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
        Wallet
      </span>
      <span className="font-mono text-[10px] text-white/30">read-only</span>
    </div>
  );
}

function Disclaimer({ pre = true }) {
  return (
    <p
      data-testid="account-wallet-disclaimer"
      className="mt-3 font-mono text-[10px] leading-relaxed text-white/40"
    >
      {pre
        ? "Informational only — ARC token contract is not deployed yet. No signature, no transaction, no token transfer is initiated from this page."
        : "Informational only — no signature, no transaction, no token transfer is initiated from this page."}
    </p>
  );
}

/**
 * AP-5B.3 — declared-vs-verified wallet badge.
 *
 * Reads `verification_status` from /api/wallet/me:
 *   - "declared" → amber: persisted but not cryptographically verified.
 *   - "verified" → green: future SIWE / AP-5C; UI is forward-compatible.
 *   - null / missing → safe fallback (treat as declared).
 *
 * Pure presentational. Does NOT prompt for a signature, does NOT call any
 * RPC, does NOT call any new API. AP-5C will add a "Verify wallet" CTA.
 */
function VerificationStatusBadge({ status }) {
  const isVerified = status === "verified";
  return (
    <div
      data-testid="account-wallet-verification-badge"
      data-verification-status={isVerified ? "verified" : "declared"}
      className={`mt-3 flex items-start gap-2 rounded-md border px-3 py-2 ${
        isVerified
          ? "border-[#39FF14]/40 bg-[#39FF14]/5 text-[#39FF14]"
          : "border-[#FFB800]/40 bg-[#FFB800]/5 text-[#FFB800]"
      }`}
    >
      {isVerified ? (
        <ShieldCheck className="h-3.5 w-3.5 mt-0.5 shrink-0" />
      ) : (
        <ShieldAlert className="h-3.5 w-3.5 mt-0.5 shrink-0" />
      )}
      <div className="leading-tight">
        <p className="font-display text-[10px] uppercase tracking-[0.2em]">
          {isVerified
            ? "Wallet verified"
            : "Declared wallet · not cryptographically verified yet"}
        </p>
        <p className="mt-0.5 font-mono text-[10px] text-white/55">
          {isVerified
            ? "Ownership proven via wallet signature."
            : "This wallet address is saved but not yet cryptographically verified. Verification only proves you can sign with this address — we do not control your keys. Verification ships in a later release."}
        </p>
      </div>
    </div>
  );
}


/**
 * Read-only chain-id probe. Calls eth_chainId on window.ethereum
 * (no signature, no permission prompt) and returns the decimal id,
 * or null if unavailable. Safe to call without any user interaction.
 */
async function getCurrentChainIdSafe() {
  try {
    if (typeof window === "undefined" || !window.ethereum) return null;
    const hex = await window.ethereum.request({ method: "eth_chainId" });
    if (!hex) return null;
    return parseInt(hex, 16);
  } catch (_e) {
    return null;
  }
}

/**
 * Save / re-save UX row. Only renders when a browser wallet is
 * connected. Three cases:
 *   - browser address differs from saved (or saved is null) → show
 *     "Save wallet to AssosPay" CTA (or "Update saved wallet").
 *   - browser address matches saved → show "Saved" confirmation +
 *     "Saved <connected_at>" timestamp.
 *   - save in flight → show spinner; on error show retry copy.
 * The save endpoint persists only the public address + chain id.
 */
function SaveWalletRow({ browserAddress, savedAddress, savedAt, saving, saveError, onSave, removing, removeError, onRemove }) {
  const matches =
    !!browserAddress &&
    !!savedAddress &&
    browserAddress.toLowerCase() === savedAddress.toLowerCase();

  const label = saving
    ? "Saving…"
    : matches
      ? "Saved"
      : savedAddress
        ? "Update saved wallet"
        : "Save wallet to AssosPay";

  const testidState = saving
    ? "saving"
    : matches
      ? "saved"
      : savedAddress
        ? "update"
        : "save";

  return (
    <div
      data-testid="account-wallet-save-row"
      data-wallet-save-state={testidState}
      className="mt-3 flex flex-wrap items-center gap-3"
    >
      {!matches && (
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          data-testid="account-wallet-save-btn"
          data-saving={saving ? "true" : "false"}
          title="Saves only your public wallet address — no signature, no transaction, no token transfer."
          className="inline-flex items-center gap-2 rounded border border-[#39FF14]/40 bg-[#39FF14]/5 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#39FF14] hover:bg-[#39FF14]/10 transition-colors disabled:cursor-not-allowed disabled:opacity-60"
        >
          {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <BookmarkPlus className="h-3 w-3" />}
          {label}
        </button>
      )}
      {matches && !saving && (
        <span
          data-testid="account-wallet-saved-badge"
          className="inline-flex items-center gap-2 rounded border border-[#39FF14]/40 bg-[#39FF14]/5 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#39FF14]"
        >
          <Check className="h-3 w-3" />
          Saved to AssosPay
        </span>
      )}
      {savedAt && (
        <span
          data-testid="account-wallet-saved-at"
          className="font-mono text-[10px] text-white/40"
        >
          since {formatSavedAt(savedAt)}
        </span>
      )}
      {savedAddress && (
        <RemoveButton
          removing={removing}
          onRemove={onRemove}
        />
      )}
      {saveError && (
        <span
          data-testid="account-wallet-save-error"
          className="font-mono text-[10px] text-[#FF3B30]"
        >
          Save failed — try again.
        </span>
      )}
      {removeError && (
        <span
          data-testid="account-wallet-remove-error"
          className="font-mono text-[10px] text-[#FF3B30]"
        >
          Remove failed — try again.
        </span>
      )}
      <span
        data-testid="account-wallet-save-disclaimer"
        className="block w-full font-mono text-[10px] text-white/35"
      >
        This only saves your public wallet address. It does not sign,
        approve, transfer, or custody tokens.{" "}
        {savedAddress
          ? "Removing only clears the saved public address from AssosPay — it does not affect your wallet or tokens."
          : ""}
      </span>
    </div>
  );
}

/**
 * When the browser has no wallet connected but the user has a saved
 * binding, surface the saved address + a remove affordance only
 * (no save button, since there's nothing to save).
 */
function SavedOnlyRow({ savedAddress, savedAt, removing, removeError, onRemove }) {
  return (
    <div
      data-testid="account-wallet-saved-only-row"
      className="mt-3 flex flex-wrap items-center gap-3"
    >
      <span
        data-testid="account-wallet-saved-address-short"
        className="font-mono text-[11px] text-white/70"
        title={savedAddress}
      >
        Saved: {shortAddress(savedAddress)}
      </span>
      {savedAt && (
        <span className="font-mono text-[10px] text-white/40">
          since {formatSavedAt(savedAt)}
        </span>
      )}
      <RemoveButton removing={removing} onRemove={onRemove} />
      {removeError && (
        <span
          data-testid="account-wallet-remove-error"
          className="font-mono text-[10px] text-[#FF3B30]"
        >
          Remove failed — try again.
        </span>
      )}
      <span
        data-testid="account-wallet-remove-disclaimer"
        className="block w-full font-mono text-[10px] text-white/35"
      >
        Removes only the saved public address from AssosPay. It does
        not affect your wallet or tokens.
      </span>
    </div>
  );
}

function RemoveButton({ removing, onRemove }) {
  const [armed, setArmed] = useState(false);
  const [timerId, setTimerId] = useState(null);

  // Reset on unmount to avoid setting state after the card disappears.
  useEffect(() => () => {
    if (timerId) clearTimeout(timerId);
  }, [timerId]);

  const handleClick = () => {
    if (removing) return;
    if (!armed) {
      // First click → arm the confirmation state for ~3 seconds.
      setArmed(true);
      const id = setTimeout(() => {
        setArmed(false);
        setTimerId(null);
      }, 3000);
      setTimerId(id);
      return;
    }
    // Second click within the window → actually remove.
    if (timerId) {
      clearTimeout(timerId);
      setTimerId(null);
    }
    setArmed(false);
    onRemove();
  };

  const state = removing ? "removing" : armed ? "armed" : "idle";
  const label = removing
    ? "removing…"
    : armed
      ? "Click again to remove"
      : "Remove";

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={removing}
      data-testid="account-wallet-remove-btn"
      data-removing={removing ? "true" : "false"}
      data-confirm-state={state}
      title="Removes only the saved public address from AssosPay. No signature, no transaction, no token transfer."
      className={`inline-flex items-center gap-1.5 rounded border px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
        armed
          ? "border-[#FF3B30]/60 bg-[#FF3B30]/10 text-[#FF3B30]"
          : "border-white/15 bg-black/40 text-white/70 hover:text-[#FF3B30] hover:border-[#FF3B30]/40"
      }`}
    >
      {removing ? (
        <Loader2 className="h-3 w-3 animate-spin" />
      ) : (
        <Trash2 className="h-3 w-3" />
      )}
      {label}
    </button>
  );
}

function formatSavedAt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return "—";
  }
}
